import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { getImageDimensionsByImageUrl } from "../../../../DesignEditor/views/DesignEditor/utils/smartImageUtils";
import { useBrushOnCanvas } from "../../../../hooks/brush/useBrushOnCanvas";
import { useAppStore } from "../../../../hooks/useStores";
import type { AutomotiveImageSegmentation, AutomotiveReflectionLayer } from "../../../../models/playground";
import styles from "./AutomotiveCanvas.module.scss";
import clsx from "clsx";

type AutomotiveCanvasProps = {
	baseImageUrl: string;
	reflectionLayers: AutomotiveReflectionLayer[] | undefined;
	canvasRef: (ref: HTMLCanvasElement | null) => void;
	canvasOverlayRef: (ref: HTMLCanvasElement | null) => void;
};

const AutomotiveCanvas = ({ baseImageUrl, reflectionLayers, canvasRef, canvasOverlayRef }: AutomotiveCanvasProps) => {
	const layerCanvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
	const [width, setWidth] = useState<number>();
	const [height, setHeight] = useState<number>();
	const { playgroundStore, imageToImageStore } = useAppStore();
	const { automotiveStore } = playgroundStore;
	const { reflectionStore } = automotiveStore;

	const containerRef = useRef<null | HTMLDivElement>(null);
	const loadingContainerRef = useRef<HTMLDivElement | null>(null);
	const localCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const localCanvasOverlayRef = useRef<null | HTMLCanvasElement>(null);

	const selectedSegmentation = playgroundStore.selectedSegmentation;
	const brushConfigs = imageToImageStore.brushConfigs;

	const { clearBrushOverlayCanvasRef } = useBrushOnCanvas(brushConfigs, localCanvasRef, localCanvasOverlayRef);

	useEffect(() => {
		getImageDimensions();
		clearBrushOverlayCanvasRef();
	}, [baseImageUrl]);

	useEffect(() => {
		if (canvasRef && canvasOverlayRef && localCanvasRef.current && localCanvasOverlayRef.current) {
			canvasRef(localCanvasRef.current);
			canvasOverlayRef(localCanvasOverlayRef.current);
		}
		const canvas = localCanvasRef.current;
		const context = canvas?.getContext("2d");
		const container = containerRef.current;

		if (!canvas || !context || !container) {
			return;
		}

		const updateCanvasSize = () => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.src = baseImageUrl;

			img.onload = () => {
				const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();

				const imgAspectRatio = img.width / img.height;
				let drawWidth = img.width;
				let drawHeight = img.height;

				if (drawWidth > containerWidth) {
					drawWidth = containerWidth;
					drawHeight = drawWidth / imgAspectRatio;
				}

				if (drawHeight > containerHeight) {
					drawHeight = containerHeight;
					drawWidth = drawHeight * imgAspectRatio;
				}

				canvas.width = drawWidth;
				canvas.height = drawHeight;
				if (localCanvasOverlayRef && localCanvasOverlayRef.current) {
					localCanvasOverlayRef.current.width = drawWidth;
					localCanvasOverlayRef.current.height = drawHeight;
				}

				if (loadingContainerRef && loadingContainerRef.current) {
					loadingContainerRef.current.style.width = `${drawWidth}px`;
					loadingContainerRef.current.style.height = `${drawHeight}px`;
				}
				context.clearRect(0, 0, canvas.width, canvas.height);
				context.drawImage(img, 0, 0, drawWidth, drawHeight);
			};
		};
		clearBrushOverlayCanvasRef();

		updateCanvasSize();
		window.addEventListener("resize", updateCanvasSize);

		return () => {
			window.removeEventListener("resize", updateCanvasSize);
			if (canvasRef) {
				canvasRef(null);
			}
		};
	}, [baseImageUrl, selectedSegmentation]);

	const getImageDimensions = async () => {
		const dimensions = await getImageDimensionsByImageUrl(baseImageUrl);
		if (dimensions) {
			setWidth(dimensions.width);
			setHeight(dimensions.height);
		}
	};

	const drawnLayersRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!reflectionLayers || !width || !height) return;

		reflectionLayers.forEach((layer) => {
			const canvas = layerCanvasRefs.current.get(layer.name);
			if (!canvas) return;

			const isVisible = !layer.hidden;
			const isDrawn = drawnLayersRef.current.has(layer.name);

			if (isVisible && !isDrawn) {
				drawSingleLayer(layer);
				drawnLayersRef.current.add(layer.name);
			} else if (!isVisible && isDrawn) {
				drawnLayersRef.current.delete(layer.name);
			} else if (isVisible && isDrawn) {
				canvas.style.opacity = `${layer.intensity}`;
			}
		});
	}, [reflectionLayers, width, height]);

	const drawSingleLayer = async (layer: AutomotiveReflectionLayer | AutomotiveImageSegmentation) => {
		if (!width || !height || !layer.url) return;

		const canvas = layerCanvasRefs.current.get(layer.name);
		if (!canvas) return;

		if (canvas.getAttribute("data-url") === layer.url) {
			canvas.style.opacity = `${(layer as AutomotiveReflectionLayer).intensity}`;
			return;
		}
		reflectionStore.selectedSegmentationRef = layerCanvasRefs;

		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.clearRect(0, 0, width, height);

		try {
			await new Promise<void>((resolve, reject) => {
				const layerUrl = layer.url as string;
				const img = new Image();
				img.crossOrigin = "anonymous";
				img.src = layerUrl;
				img.onload = () => {
					// Check if this is a segmentation layer
					if (layer === selectedSegmentation) {
						// Draw the image to an offscreen canvas first to get pixel data
						const offscreenCanvas = document.createElement("canvas");
						offscreenCanvas.width = width;
						offscreenCanvas.height = height;
						const offscreenCtx = offscreenCanvas.getContext("2d");

						if (offscreenCtx) {
							// Draw the original image
							offscreenCtx.drawImage(img, 0, 0, width, height);

							// Get the image data
							const imageData = offscreenCtx.getImageData(0, 0, width, height);
							const data = imageData.data;

							// Process the pixels
							for (let i = 0; i < data.length; i += 4) {
								const r = data[i];
								const g = data[i + 1];
								const b = data[i + 2];

								// Check if pixel is black or near black (allowing some tolerance)
								if (r < 30 && g < 30 && b < 30) {
									// Make black pixels transparent
									data[i + 3] = 0; // Set alpha to 0
								} else if (r > 200 && g > 200 && b > 200) {
									// Change white or near-white pixels to red
									data[i] = 117; // Red
									data[i + 1] = 51; // Green
									data[i + 2] = 211; // Blue
									data[i + 3] = 150; // Full opacity
								}
							}

							// Put the modified image data back on the main canvas
							ctx.putImageData(imageData, 0, 0);
						} else {
							// Fallback to normal drawing if offscreen context fails
							ctx.globalAlpha = 1;
							ctx.drawImage(img, 0, 0, width, height);
						}
					} else {
						// Normal layer drawing
						ctx.globalAlpha = 1;
						ctx.drawImage(img, 0, 0, width, height);
					}

					canvas.setAttribute("data-url", layerUrl);
					resolve();
				};
				img.onerror = reject;

				setTimeout(resolve, 5000);
			});
		} catch (error) {
			console.error(`Failed to load image for layer ${layer.name}:`, error);
		}
	};

  const zoom = reflectionStore.reflectionZoom;


	return (
		<Box className={clsx(styles.canvasContainer, "automotive-canvas-container")} ref={containerRef}>
			{selectedSegmentation && (
				<Box
					className={styles.customCursor}
					id="customBrushCursor"
					style={{
						width: brushConfigs.lineWidth,
						height: brushConfigs.lineWidth,
						backgroundColor: brushConfigs.strokeStyle,
						opacity: brushConfigs.brushColorOpacity,
					}}
				></Box>
			)}

			<canvas
				onContextMenu={(e) => e.preventDefault()}
				className={styles.briaCanvas}
				ref={localCanvasRef}
				style={{ pointerEvents: selectedSegmentation ? "auto" : "none" }}
			/>
			<canvas onContextMenu={(e) => e.preventDefault()} ref={localCanvasOverlayRef} style={{ transform: `scale(${zoom})` }}/>

			<img src={baseImageUrl || "/placeholder.svg"} className={styles.baseCanvas} />
			{selectedSegmentation && (
				<canvas
					key={`${selectedSegmentation.name}_segmentation`}
					ref={(el) => {
						if (el) {
							layerCanvasRefs.current.set(selectedSegmentation.name, el);
							if (width && height && selectedSegmentation.url) {
								drawSingleLayer(selectedSegmentation);
							}
						}
					}}
					className={styles.layerCanvas}
          style={{ transform: `scale(${zoom})` }}
				/>
			)}
			{!selectedSegmentation &&
				reflectionLayers
					?.filter((layer) => !layer.hidden)
					.map((layer) => (
						<canvas
							key={layer.name}
							ref={(el) => {
								if (el) {
									layerCanvasRefs.current.set(layer.name, el);
									if (width && height && layer.url) {
										drawSingleLayer(layer);
									}
								}
							}}
							className={styles.layerCanvas}
							style={{ opacity: layer.intensity }}
						/>
					))}
		</Box>
	);
};

export default observer(AutomotiveCanvas);
